import React, { useEffect, useState, useRef } from "react";
import { Container, Row, Col, Card, Badge, Spinner, Alert, Form, Button, Image } from "react-bootstrap";
import styled from "styled-components";
import dayjs from "dayjs";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useParams } from "react-router-dom";
import { Skeleton } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import axios from "axios";
import Localbase from "localbase";
import ENDPOINT from "../../context/EndPoint";

// --- Styled Components ---
const SubmissionCard = styled(Card)`
  margin-bottom: 2rem;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.07);
  border-radius: 14px;
`;

const SectionTitle = styled.h5`
  color: #333;
  border-left: 4px solid #0d6efd;
  padding-left: 10px;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
`;

const StatusBadge = styled(Badge)`
  font-size: 0.95em;
  margin-right: 6px;
  background: ${props => props.status === 'pending' ? '#ffc107' : props.status === 'evaluated' ? '#28a745' : '#dc3545'} !important;
  color: #fff !important;
`;

const ProfileImage = styled(Image)`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 50%;
  margin-right: 10px;
`;

const LeaderboardContainer = styled.div`
  font-family: "Poppins", sans-serif;
  color: #fff;
`;

const TopThreeWrapper = styled.div`
  background: #23233f;
  border-radius: 32px;
  margin: 0 auto 20px auto;
  padding: 28px 0 50px 0;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: center;
  gap: 14px;
  position: relative;
  box-shadow: 0 6px 24px 0 rgba(20, 23, 71, 0.16);
`;

const TopUserCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  min-width: 120px;
  min-height: ${(props) => (props.rank === 1 ? "200px" : "170px")};
`;

const AvatarBorder = styled.div`
  background: ${(props) =>
    props.rank === 1
      ? "linear-gradient(135deg, #ffdb7a, #ffb800)"
      : props.rank === 2
      ? "linear-gradient(135deg, #679cff, #2e4fff)"
      : "linear-gradient(135deg, #b6ffd7, #2ee69d)"};
  border-radius: 50%;
  padding: ${(props) => (props.rank === 1 ? "6px" : "3.5px")};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${(props) => (props.rank === 1 ? "0" : "0")};
  position: relative;
  width: ${(props) => (props.rank === 1 ? "88px" : "64px")};
  height: ${(props) => (props.rank === 1 ? "88px" : "64px")};
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background: #fff;
`;

const CrownIcon = styled(EmojiEventsIcon)`
  && {
    color: #ffb800;
    font-size: 2.6rem;
    position: absolute;
    top: -42px;
    left: 50%;
    transform: translateX(-50%);
    filter: drop-shadow(0 2px 6px #0006);
  }
`;

const TopName = styled.div`
  margin-top: ${(props) => (props.rank === 1 ? "14px" : "10px")};
  font-size: ${(props) => (props.rank === 1 ? "1.18rem" : "1rem")};
  font-weight: 600;
  text-align: center;
`;

const TopScore = styled.div`
  color: ${(props) =>
    props.rank === 1
      ? "#ffb800"
      : props.rank === 2
      ? "#4b9fff"
      : "#27e89d"};
  font-size: ${(props) => (props.rank === 1 ? "1.35rem" : "1.07rem")};
  font-weight: 700;
  text-align: center;
`;

const OtherUsername = styled.div`
  font-size: 0.83rem;
  color: #7c7da2;
`;

const OthersSection = styled.div`
  background: #23233f;
  border-radius: 32px;
  margin: 0 auto;
  padding: 28px 0 8px 0;
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  padding: 15px 24px 15px 24px;
  border-bottom: 1px solid #222244;
  transition: background 0.22s, box-shadow 0.22s, transform 0.5s cubic-bezier(
      .68, -0.55, .27, 1.55
    );
  cursor: pointer;
  position: relative;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #23234f;
    box-shadow: 0 8px 32px 0 rgba(39, 232, 157, 0.15);
    transform: translateY(-6px) scale(1.03) rotate(-1deg);
    z-index: 2;
    animation: bounceRow 0.7s cubic-bezier(.68,-0.55,.27,1.55);
  }
  @keyframes bounceRow {
    0% { transform: translateY(0) scale(1) rotate(0deg); }
    40% { transform: translateY(-12px) scale(1.04) rotate(-2deg); }
    60% { transform: translateY(-6px) scale(1.03) rotate(-1deg); }
    100% { transform: translateY(-6px) scale(1.03) rotate(-1deg); }
  }
`;

const OtherAvatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 16px;
  background: #222244;
`;

const OtherNameBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const OtherName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OtherScore = styled.div`
  font-size: 1.03rem;
  font-weight: 600;
  color: #27e89d;
  margin-right: 10px;
`;

const SearchBarWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 32px auto;
  position: relative;
  z-index: 2;
  width: 500px;
  max-width: 90vw;
`;

const AnimatedSearchBar = styled.input`
  width: 100%;
  padding: 14px 48px 14px 20px;
  border-radius: 18px;
  border: none;
  background: linear-gradient(90deg, #23233f 60%, #1a1a2e 100%);
  color: #fff;
  font-size: 1.15rem;
  font-family: "Poppins", sans-serif;
  box-shadow: 0 4px 24px 0 rgba(39, 232, 157, 0.08);
  outline: none;
  transition: box-shadow 0.3s, background 0.3s, width 0.4s cubic-bezier(
      .68, -0.55, .27, 1.55
    );
  border: 2px solid #23234f;
  animation: searchPulse 2.5s infinite alternate;
  &:focus {
    box-shadow: 0 8px 32px 0 rgba(39, 232, 157, 0.18);
    background: linear-gradient(90deg, #23234f 60%, #27e89d 100%);
    border: 2px solid #27e89d;
    width: 100%;
  }
  &::placeholder {
    color: #7c7da2;
    font-size: 1.08rem;
    letter-spacing: 0.5px;
    opacity: 0.9;
    transition: color 0.3s;
  }
  @keyframes searchPulse {
    0% { box-shadow: 0 4px 24px 0 rgba(39,232,157,0.08);}
    100% { box-shadow: 0 8px 32px 0 rgba(39,232,157,0.18);}
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #27e89d;
  font-size: 1.5rem;
  pointer-events: none;
  transition: color 0.3s;
  animation: iconBounce 2s infinite alternate;
  display: flex;
  align-items: center;
  @keyframes iconBounce {
    0% { transform: translateY(-50%) scale(1);}
    100% { transform: translateY(-60%) scale(1.12);}
  }
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #435185, #435185);
  color: white;

  // &:hover {
  //   background: linear-gradient(135deg, #435185, #679cff);
  // }

  svg {
    margin-left: 8px;
    width: 20px;
    height: 20px;
  }
`;

const FloatingLeaderboard = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #23233f;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 6px 24px 0 rgba(20, 23, 71, 0.16);
  width: 220px;
  z-index: 1000;
  cursor: pointer;
  transition: transform 0.3s ease, opacity 0.3s ease;
  opacity: ${props => (props.visible ? 1 : 0)};
  transform: ${props => (props.visible ? 'translateY(0)' : 'translateY(20px)')};
  pointer-events: ${props => (props.visible ? 'auto' : 'none')};

  &:hover {
    transform: ${props => (props.visible ? 'translateY(-5px)' : 'translateY(20px)')};
    box-shadow: 0 8px 32px 0 rgba(39, 232, 157, 0.18);
  }
`;

const FloatingTopThreeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FloatingTopUserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FloatingAvatarBorder = styled.div`
  background: ${props =>
    props.rank === 1
      ? "linear-gradient(135deg, #ffdb7a, #ffb800)"
      : props.rank === 2
      ? "linear-gradient(135deg, #679cff, #2e4fff)"
      : "linear-gradient(135deg, #b6ffd7, #2ee69d)"};
  border-radius: 50%;
  padding: 2px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FloatingAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background: #fff;
`;

const FloatingCrownIcon = styled(EmojiEventsIcon)`
  && {
    color: #ffb800;
    font-size: 1.2rem;
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const FloatingTopName = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
  flex: 1;
`;

const FloatingTopScore = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props =>
    props.rank === 1 ? "#ffb800" : props.rank === 2 ? "#4b9fff" : "#27e89d"};
`;

const SubmissionSkeleton = () => (
  <Col xs={12}>
    <SubmissionCard>
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <Skeleton variant="circular" width={40} height={40} className="me-3" />
          <div>
            <Skeleton variant="text" width={150} height={24} />
            <Skeleton variant="text" width={120} height={18} />
          </div>
        </div>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" className="mb-3" />
        <SectionTitle style={{ border: 'none', paddingLeft: 0 }}>
          <Skeleton variant="text" width="40%" height={30} />
        </SectionTitle>
        <div className="mb-3">
          <Skeleton variant="rounded" width={90} height={28} />
        </div>
        <SectionTitle style={{ border: 'none', paddingLeft: 0 }}>
          <Skeleton variant="text" width="50%" height={30} />
        </SectionTitle>
        <Skeleton variant="text" width="15%" className="mb-1" />
        <Skeleton variant="rectangular" height={38} className="mb-3" />
        <Skeleton variant="text" width="15%" className="mb-1" />
        <Skeleton variant="rectangular" height={38} className="mb-3" />
        <Skeleton variant="rounded" width={150} height={38} />
      </Card.Body>
      <Card.Footer>
        <Skeleton variant="text" width="60%" />
      </Card.Footer>
    </SubmissionCard>
  </Col>
);

const ChallengeSubmissionsList = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateLoading, setUpdateLoading] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const { challenge_id } = useParams();
  const [leaderboard, setLeaderboard] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [initialData, setInitialData] = useState([]);
  const [response, setResponse] = useState([]);
  const [showOthers, setShowOthers] = useState(false);
  const [showFloatingLeaderboard, setShowFloatingLeaderboard] = useState(false);
  const leaderboardRef = useRef(null);
  let db = new Localbase("db");

  const handleToggleOthers = () => {
    setShowOthers(!showOthers);
  };

  const handleScrollToLeaderboard = () => {
    leaderboardRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleScroll = () => {
    if (leaderboardRef.current) {
      const leaderboardRect = leaderboardRef.current.getBoundingClientRect();
      const isLeaderboardOutOfView = leaderboardRect.bottom < 0;
      setShowFloatingLeaderboard(isLeaderboardOutOfView && leaderboard.length > 0);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [leaderboard]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        ENDPOINT +`get-hacking-challenge-submissions.php?challenge_id=${challenge_id}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setSubmissions(data.data);
      } else {
        setError(data.message || "Failed to load submissions.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    const formData = new FormData();
    formData.append("challenge_id", challenge_id);

    try {
      const res = await axios.post(
        "https://siliconmindstechnologies.com/challenge-submission-leaderboard.php",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const data = res.data;
      if (data.status === "success" && data.leaderboard) {
        setLeaderboard(data.leaderboard);
        setInitialData(data.leaderboard.slice(3));
        setResponse(data.leaderboard.slice(3));
        await db.collection("paths").add(
          { challenge: data.leaderboard },
          "leaderboard"
        );
      } else {
        console.error("Error fetching leaderboard:", data.message);
      }
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    }
  };

  const handleUpdateSubmit = async (submission_id, challenge_id, e) => {
    e.preventDefault();
    setUpdateLoading((prev) => ({ ...prev, [submission_id]: true }));
    const formData = new FormData(e.target);
    formData.append("submission_id", submission_id);
    formData.append("challenge_id", challenge_id);

    try {
      const res = await fetch(ENDPOINT +"update-challenge-submission.php", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.status === "success") {
        setSubmissions((submissions) =>
          submissions.map((sub) =>
            sub.submission_id === submission_id && sub.challenge_id === challenge_id
              ? { ...sub, status: formData.get("status"), marks: formData.get("marks") }
              : sub
          )
        );
        setSnackbar({ open: true, message: "Submission updated successfully.", severity: "success" });
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to update submission.", severity: "error" });
      }
    } catch {
      setSnackbar({ open: true, message: "Network error.", severity: "error" });
    } finally {
      setUpdateLoading((prev) => ({ ...prev, [submission_id]: false }));
    }
  };

  const handleSearch = (e) => {
    const rawValue = e.target.value;
    setSearchTerm(rawValue);
    const term = rawValue.toLowerCase();
    if (initialData) {
      const filteredData = initialData.filter(
        (item) =>
          item.first_name.toLowerCase().includes(term) ||
          item.last_name.toLowerCase().includes(term) ||
          item.username.toLowerCase().includes(term)
      );
      setResponse(filteredData);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [challenge_id]);

  useEffect(() => {
    db.collection("paths")
      .doc("challenge-leaderboard")
      .get()
      .then((doc) => {
        if (doc && doc.leaderboard) {
          setLeaderboard(doc.leaderboard);
          setInitialData(doc.leaderboard);
          setResponse(doc.leaderboard);
        }
      })
      .catch((error) => {
        console.error("Error fetching data from Localbase", error);
      });
    fetchLeaderboard();
  }, []);

  if (loading) {
    return (
      <Container className="py-4">
        <h2 className="mb-4 text-center">
          <Skeleton variant="text" width={250} height={40} className="mx-auto" />
        </h2>
        <Row>
          <SubmissionSkeleton />
          <SubmissionSkeleton />
          <SubmissionSkeleton />
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div ref={leaderboardRef}>
        <LeaderboardContainer>
          <h1 className="text-center mb-1">Assignment Leaderboard</h1>
          <p className="text-center text-black mb-4">Top performers in this assignment</p>
          {/* <SearchBarWrapper>
            <AnimatedSearchBar
              type="text"
              placeholder="Search students by name or username…"
              value={searchTerm}
              onChange={handleSearch}
            />
            <SearchIcon>
              <svg width="24" height="24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="#27e89d" strokeWidth="2" />
                <path
                  d="M20 20L16.65 16.65"
                  stroke="#27e89d"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </SearchIcon>
          </SearchBarWrapper> */}
          {Array.isArray(leaderboard) && leaderboard.length > 0 ? (
            <TopThreeWrapper>
              {leaderboard[1] && (
                <TopUserCard rank={2}>
                  <AvatarBorder rank={2}>
                    <AvatarImg
                      src={leaderboard[1].profile_pic}
                      alt={leaderboard[1].first_name}
                    />
                  </AvatarBorder>
                  <TopName rank={2}>{leaderboard[1].first_name}</TopName>
                  <TopScore rank={2}>{leaderboard[1].marks}</TopScore>
                </TopUserCard>
              )}
              {leaderboard[0] && (
                <TopUserCard rank={1} style={{ zIndex: 1 }}>
                  <CrownIcon />
                  <AvatarBorder rank={1}>
                    <AvatarImg
                      src={leaderboard[0].profile_pic}
                      alt={leaderboard[0].first_name}
                    />
                  </AvatarBorder>
                  <TopName rank={1}>{leaderboard[0].first_name}</TopName>
                  <TopScore rank={1}>{leaderboard[0].marks}</TopScore>
                </TopUserCard>
              )}
              {leaderboard[2] && (
                <TopUserCard rank={3}>
                  <AvatarBorder rank={3}>
                    <AvatarImg
                      src={leaderboard[2].profile_pic}
                      alt={leaderboard[2].first_name}
                    />
                  </AvatarBorder>
                  <TopName rank={3}>{leaderboard[2].first_name}</TopName>
                  <TopScore rank={3}>{leaderboard[2].marks}</TopScore>
                </TopUserCard>
              )}
              <div className="mt-3 w-100">
                {Array.isArray(leaderboard) && leaderboard.length > 3 && (
                  <ToggleButton onClick={handleToggleOthers}>
                    {showOthers ? "Hide Others" : "Show Others"}
                    <svg
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d={showOthers ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
                      />
                    </svg>
                  </ToggleButton>
                )}
              </div>
            </TopThreeWrapper>
          ) : null}
          {showOthers && Array.isArray(leaderboard) && leaderboard.length > 3 ? (
            <OthersSection className="col-lg-12 col-md-6 col-sm-12 col-12 mb-3">
              {response.map((user, idx) => (
                <UserRow key={user.user_id}>
                  <div>
                    <h6 className="text-warning me-2">#{idx + 4}</h6>
                  </div>
                  <OtherAvatar src={user.profile_pic} alt={user.first_name} />
                  <OtherNameBlock>
                    <OtherName>
                      {user.first_name} {user.last_name}
                    </OtherName>
                    <OtherUsername>@{user.username}</OtherUsername>
                  </OtherNameBlock>
                  <OtherScore>
                    <span className="text-white p">Points: </span>
                    {user.marks || 0}
                  </OtherScore>
                </UserRow>
              ))}
            </OthersSection>
          ) : null}
        </LeaderboardContainer>
      </div>
      <h2 className="mb-4 text-center">Submissions List</h2>
      <Row>
        {submissions.map((submission) => (
          <Col xs={12} key={submission.submission_id}>
            <SubmissionCard>
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  {submission.profile_pic ? (
                    <ProfileImage src={submission.profile_pic} alt={`${submission.first_name} ${submission.last_name}`} />
                  ) : (
                    <ProfileImage src="https://via.placeholder.com/40" alt="No profile picture" />
                  )}
                  <div>
                    <Card.Title className="mb-1">
                      {submission.first_name} {submission.last_name}
                    </Card.Title>
                    <Card.Subtitle className="text-muted small">
                      Phone: {submission.phone_number || "N/A"}
                    </Card.Subtitle>
                  </div>
                </div>
                <Card.Text className="mb-3">
                  <strong>Challenge ID:</strong> {submission.challenge_id}<br />
                  <strong>File:</strong> <a href={submission.file_path} target="_blank" rel="noopener noreferrer">{submission.file_name}</a>
                </Card.Text>
                <SectionTitle>Submission Details</SectionTitle>
                <div className="mb-3">
                  <StatusBadge status={submission.status}>{submission.status}</StatusBadge>
                  {submission.marks && <Badge bg="info">Marks: {submission.marks}</Badge>}
                </div>
                <SectionTitle>Update Submission</SectionTitle>
                <Form onSubmit={(e) => handleUpdateSubmit(submission.submission_id, submission.challenge_id, e)}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select name="status" defaultValue={submission.status} required>
                      <option value="pending">Pending</option>
                      <option value="evaluated">Evaluated</option>
                      <option value="rejected">Rejected</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Marks</Form.Label>
                    <Form.Control
                      type="number"
                      name="marks"
                      defaultValue={submission.marks || ""}
                      placeholder="Enter marks"
                      min="0"
                      max="100"
                      required
                    />
                  </Form.Group>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={updateLoading[submission.submission_id]}
                  >
                    {updateLoading[submission.submission_id] ? "Updating..." : "Update Submission"}
                  </Button>
                </Form>
              </Card.Body>
              <Card.Footer className="text-muted small d-flex justify-content-between align-items-center">
                <span>
                  Submitted: {dayjs(submission.created_at).format("DD MMM YYYY, HH:mm")}
                  {submission.updated_at && <> | Updated: {dayjs(submission.updated_at).format("DD MMM YYYY, HH:mm")}</>}
                </span>
              </Card.Footer>
            </SubmissionCard>
          </Col>
        ))}
      </Row>
      {submissions.length === 0 && <div className="text-center text-muted py-5">No submissions found for this challenge.</div>}
      <FloatingLeaderboard visible={showFloatingLeaderboard} onClick={handleScrollToLeaderboard}>
        <FloatingTopThreeWrapper>
        {leaderboard[0] && (
            <FloatingTopUserCard rank={1}>
              <FloatingAvatarBorder rank={1}>
                <FloatingAvatarImg
                  src={leaderboard[0].profile_pic}
                  alt={leaderboard[0].first_name}
                />
                <FloatingCrownIcon />
              </FloatingAvatarBorder>
              <FloatingTopName>{leaderboard[0].first_name}</FloatingTopName>
              <FloatingTopScore rank={1}>{leaderboard[0].marks}</FloatingTopScore>
            </FloatingTopUserCard>
          )}
          {leaderboard[1] && (
            <FloatingTopUserCard rank={2}>
              <FloatingAvatarBorder rank={2}>
                <FloatingAvatarImg
                  src={leaderboard[1].profile_pic}
                  alt={leaderboard[1].first_name}
                />
              </FloatingAvatarBorder>
              <FloatingTopName>{leaderboard[1].first_name}</FloatingTopName>
              <FloatingTopScore rank={2}>{leaderboard[1].marks}</FloatingTopScore>
            </FloatingTopUserCard>
          )}
         
          {leaderboard[2] && (
            <FloatingTopUserCard rank={3}>
              <FloatingAvatarBorder rank={3}>
                <FloatingAvatarImg
                  src={leaderboard[2].profile_pic}
                  alt={leaderboard[2].first_name}
                />
              </FloatingAvatarBorder>
              <FloatingTopName>{leaderboard[2].first_name}</FloatingTopName>
              <FloatingTopScore rank={3}>{leaderboard[2].marks}</FloatingTopScore>
            </FloatingTopUserCard>
          )}
        </FloatingTopThreeWrapper>
      </FloatingLeaderboard>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};

export default ChallengeSubmissionsList;